import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {BehaviorSubject, exhaustMap, Observable, take, tap, map} from "rxjs";
import {AuthenticationService} from "./authentication.service";
import {
  ActionResult,
  ActionResultList,
  FetchRaceByIdRequest,
  FindRacesRequest,
  Race, RaceRegistrationRequestDto, RaceStatus,
  UserDetail,
  RaceRegistrationResultDto, PagedFilter, RegistrationFilter, RaceRegistration,
} from "@model";
import {LoaderService} from "./loader.service";
import {EnvironmentService} from "./environment.service";

@Injectable({providedIn: 'root'})
export class RaceService {

  private static _recommendedRaces: BehaviorSubject<ActionResultList<Race[], number> | null> = new BehaviorSubject<ActionResultList<Race[], number> | null>(null);
  private static _currentRace: BehaviorSubject<Race | undefined> = new BehaviorSubject<Race | undefined>(undefined);
  private static _races: BehaviorSubject<ActionResultList<Race[], number> | null> = new BehaviorSubject<ActionResultList<Race[], number> | null>(null);
  private static _registerToRaceUserData: BehaviorSubject<UserDetail | null> = new BehaviorSubject<UserDetail | null>(null);

  get recommendedRaces(): BehaviorSubject<ActionResultList<Race[], number> | null> {
    return RaceService._recommendedRaces;
  }

  get currentRace(): BehaviorSubject<Race | undefined> {
    return RaceService._currentRace;
  }

  get races(): BehaviorSubject<ActionResultList<Race[], number> | null> {
    return RaceService._races;
  }

  get registerToRaceUserData(): BehaviorSubject<UserDetail | null> {
    return RaceService._registerToRaceUserData;
  }

  constructor(private http: HttpClient, private authenticationService: AuthenticationService, private loaderService: LoaderService,
              private environmentService:EnvironmentService) {
  }

  setCurrentRace(race: Race): void {
    this.currentRace.next(race);
  }

  findRecommendedRaces(): void {
    this.authenticationService.jwtResponse.pipe(take(1), exhaustMap(() => {
      return this.http.post<ActionResultList<Race[], number>>(this.environmentService.getApiUrl() + "/api/races/filter/recommended", FindRacesRequest.createRecommendRacesFilter());
    })).subscribe((actionResultList: ActionResultList<Race[], number>) => {
      this.recommendedRaces.next(actionResultList);
    });
  }


  searchUserRaces(filter: FindRacesRequest): Observable<Race[]> {
    return this.http.post<ActionResultList<Race[], number>>(this.environmentService.getApiUrl() + "/api/races/filter", filter).pipe(map((value: ActionResultList<Race[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  copyRace(race: Race, onSuccess: (raceId: number) => void, onError: () => void): void {
    this.http.post<ActionResult<number>>(this.environmentService.getApiUrl() + "/api/copy/race", race).subscribe((actionResult: ActionResult<number>) => {
      if (actionResult.resultCode !== -1 && actionResult.result) {
        onSuccess(actionResult.result)
      } else {
        onError();
      }
    });
  }

  updateRace(race: Race, onSuccess: (raceId: number) => void, onError: () => void): void {
    this.http.post<ActionResult<number>>(this.environmentService.getApiUrl() + (race.id ? "/api/races/update" : "/api/races/add"), race).subscribe((actionResult: ActionResult<number>) => {
      if (actionResult.resultCode !== -1 && actionResult.result) {
        onSuccess(actionResult.result)
      } else {
        onError();
      }
    });
  }

  deleteRace(race: Race, onSuccess: () => void, onError: () => void): void {
    this.loaderService.startLoading();
    this.http.post<ActionResult<boolean>>
    (this.environmentService.getApiUrl() + "/api/races/delete", race.id)
      .subscribe((actionResult: ActionResult<boolean>) => {
        if (actionResult.resultCode !== -1) {
          this.loaderService.dismissLoading();
          onSuccess();
        } else {
          this.loaderService.dismissLoading();
          onError();
        }
      });
  }

  searchRaces(filter: FindRacesRequest): Observable<ActionResultList<Race[], number>> {
    return this.http.post<ActionResultList<Race[], number>>(this.environmentService.getApiUrl() + "/api/public/races/filter", filter);
  }

  findRaceByIdProtected(raceRequest: FetchRaceByIdRequest, hideLoading?: boolean): Observable<Race | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<Race>>(this.environmentService.getApiUrl() + "/api/races/getracebyid", raceRequest)
      .pipe(
        tap(() => {
          if (!hideLoading) {
            this.loaderService.dismissLoading()
          }
        }),
        map((value: ActionResult<Race>) => {
          if (value && value.result) {
            return value.result;
          } else {
            return null;
          }
        })
      );
  }

  fetchRaceToCopy(raceRequest: FetchRaceByIdRequest, hideLoading?: boolean): Observable<Race | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<Race>>(this.environmentService.getApiUrl() + "/api/races/getracebyid", raceRequest)
      .pipe(
        tap(() => {
          if (!hideLoading) {
            this.loaderService.dismissLoading()
          }
        }),
        map((value: ActionResult<Race>) => {
          if (value && value.result) {
            let race: Race = value.result;
            race.status = RaceStatus.INACTIVE
            race.raceStart = undefined;
            race.raceEnd = undefined;
            race.registrationEnd = undefined;
            race.registrationStart = undefined;
            return value.result;
          } else {
            return null;
          }
        })
      );
  }

  findRaceById(raceRequest: FetchRaceByIdRequest, hideLoading?: boolean): Observable<Race | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<Race>>(this.environmentService.getApiUrl() + "/api/public/races/getracebyid", raceRequest)
      .pipe(
        tap(() => {
          if (!hideLoading) {
            this.loaderService.dismissLoading()
          }
        }),
        map((value: ActionResult<Race>) => {
          if (value && value.result) {
            this.currentRace.next(value.result);
            return value.result;
          } else {
            return null;
          }
        })
      );
  }

  findRace(raceRequest: FetchRaceByIdRequest, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<Race>>(this.environmentService.getApiUrl() + "/api/public/races/getracebyid", raceRequest)
      .subscribe((actionResult: ActionResult<Race>) => {
        this.currentRace.next(actionResult.result);
      });
  }

  registerToRace(registerRaceRequest: RaceRegistrationRequestDto, registrationWithoutPayment: boolean, onSuccess: (registerToRaceResponse: RaceRegistrationResultDto) => void, onError: (error: RegisterToRaceError) => void): void {
    this.loaderService.startLoading();
    let url: string;
    if (registrationWithoutPayment) {
      url = "/api/races/organiser/register"
    } else {
      url = this.authenticationService.isAuthenticated() ? "/api/races/registertorace" : "/api/public/races/registertorace"
    }
    this.http.post<ActionResult<any>>(this.environmentService.getApiUrl() + url, registerRaceRequest).pipe(tap(() => {
      this.loaderService.dismissLoading()
    })).subscribe((actionResult: ActionResult<any>) => {
        if (actionResult.resultCode === RegisterToRaceError.USER_ALREADY_REGISTERED_TO_RACE) {
          onError(RegisterToRaceError.USER_ALREADY_REGISTERED_TO_RACE);
        } else if (actionResult.success === false) {
          onError(RegisterToRaceError.REGISTERED_TO_RACE_ERROR);
        } else {
          onSuccess(actionResult.result!);
        }
      },
      () => {
        this.loaderService.dismissLoading();
        onError(RegisterToRaceError.REGISTERED_TO_RACE_ERROR);
      });
  }

  calculateRaceItemPrices(registerRaceRequest: RaceRegistrationRequestDto, onSuccess: (registerToRaceResponse: RaceRegistrationResultDto) => void, onError: () => void): void {
    this.loaderService.startLoading();
    let url: string = this.authenticationService.isAuthenticated() ? "/api/races/calculate" : "/api/public/races/calculate"
    this.http.post<ActionResult<any>>(this.environmentService.getApiUrl() + url, registerRaceRequest).pipe(tap(() => {
      this.loaderService.dismissLoading()
    })).subscribe((actionResult: ActionResult<any>) => {
      if (actionResult.success === true) {
        onSuccess(actionResult.result!);
      } else {
        onError();
      }
    });
  }

  emptyRaces(): void {
    this.recommendedRaces.next(null);
    this.currentRace.next(undefined);
    this.races.next(null);
  }

  emptyCurrentRace() {
    this.currentRace.next(undefined);
  }

  removeRaceFromFavorites(raceId: number | undefined): Observable<ActionResult<boolean>> {
    let endpoint: string = "/api/races/favorite/remove";
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + endpoint, raceId);
  }

  addRaceToFavorites(raceId: number): Observable<ActionResult<boolean>> {
    let endpoint: string = "/api/races/favorite/add";
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + endpoint, raceId)
  }

  fetchUserFavoritesRaces(startLoading?: boolean): Observable<Race[]> {
    return this.http.get<ActionResultList<(Race)[], number>>(this.environmentService.getApiUrl() + "/api/races/favorite/races").pipe(map((value: ActionResultList<Race[], number>) => {
        if (value && value.result && value.result.length != 0) {
          return value.result;
        } else {
          return [];
        }
      })
    );
  }

  findMatchingRegistrations(filter: PagedFilter<RegistrationFilter> | undefined): Observable<RaceRegistration[]> {
    return this.http.post<ActionResultList<RaceRegistration[], number>>(this.environmentService.getApiUrl() + "/api/organiser/registrations", filter).pipe(
      map((actionResult: ActionResultList<RaceRegistration[], number>) => {
        if (actionResult.resultCode !== -1 && actionResult.result) {
          return actionResult.result;
        } else {
          return [];
        }
      }));
  }
}

export enum RegisterToRaceError {
  USER_ALREADY_REGISTERED_TO_RACE = 10,
  REGISTERED_TO_RACE_ERROR
}
